import { HLayout, VLayout } from '@/shared/ui/layouts/flex'
import { Typography } from '@/shared/ui/typography'

import { Children, type FC, type ReactElement, cloneElement, isValidElement } from 'react'

import type { IconSize, IconTextGap, IconTextProps } from './types'

/**
 * Maps typography types and variants to appropriate icon sizes and gaps
 * Ensures visual consistency between text, icon size, and spacing
 */
const getSize = (props: IconTextProps): { iconSize: IconSize; gap: IconTextGap } => {
  const { type } = props

  // Heading sizes
  if (type === 'heading') {
    const variant = props.variant
    if (variant.startsWith('h1')) return { iconSize: 40, gap: '3' }
    if (variant.startsWith('h2')) return { iconSize: 32, gap: '3' }
    if (variant.startsWith('h3')) return { iconSize: 28, gap: '2' }
    if (variant.startsWith('h4')) return { iconSize: 24, gap: '2' }
    if (variant.startsWith('h5')) return { iconSize: 20, gap: '2' }
  }

  // Paragraph sizes
  if (type === 'paragraph') {
    const variant = props.variant || 'md'
    if (variant.startsWith('lg')) return { iconSize: 20, gap: '2' }
    if (variant.startsWith('md')) return { iconSize: 16, gap: '2' }
    if (variant.startsWith('sm')) return { iconSize: 14, gap: '2' }
    if (variant.startsWith('xs')) return { iconSize: 12, gap: '1' }
  }

  // Caption size
  if (type === 'caption') return { iconSize: 14, gap: '1' }

  // Label sizes
  if (type === 'label') {
    const variant = props.variant || 'md'
    if (variant === 'xs') return { iconSize: 12, gap: '1' }
    if (variant === 'sm') return { iconSize: 14, gap: '1' }
    if (variant === 'md') return { iconSize: 16, gap: '2' }
  }

  // Fallback
  return { iconSize: 20, gap: '2' }
}

/**
 * IconText component combines typography with an icon in a consistent way
 *
 * @example
 * <IconText type="heading" variant="h3-bold" layout="horizontal" gap="2">
 *   <CheckCircle />
 *   Success!
 * </IconText>
 *
 * @example
 * <IconText type="paragraph" variant="md" layout="vertical" gap="3">
 *   <Star />
 *   Featured
 * </IconText>
 *
 * @example
 * <IconText type="label" variant="sm" color="primary">
 *   <InfoCircle />
 *   Information label
 * </IconText>
 */
export const IconText: FC<IconTextProps> = props => {
  const {
    layout = 'horizontal',
    gap,
    iconSize: customIconSize,
    children,
    ...typographyProps
  } = props

  // Get default icon size and gap based on typography
  const { iconSize: defaultIconSize, gap: defaultGap } = getSize(props)

  // Use custom values if provided, otherwise use calculated defaults
  const finalIconSize = customIconSize ?? defaultIconSize
  const finalGap = gap ?? defaultGap

  // Separate icon from text content
  const childrenArray = Children.toArray(children)
  const iconElement = childrenArray.find(child => isValidElement(child)) as ReactElement
  const textContent = childrenArray.filter(child => !isValidElement(child) || child !== iconElement)

  // Clone icon with appropriate size
  const styledIcon = iconElement
    ? cloneElement(iconElement, {
        size: finalIconSize,
      } as Partial<typeof iconElement.props>)
    : null

  const LayoutComponent = layout === 'horizontal' ? HLayout : VLayout

  return (
    <Typography {...typographyProps}>
      <LayoutComponent gap={finalGap} items="center">
        {styledIcon}
        <span>{textContent}</span>
      </LayoutComponent>
    </Typography>
  )
}
